import { useMemo } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { components, type OptionProps } from "react-select";
import { MdRotateLeft } from "react-icons/md";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import Loading from "../../../../components/core/blocks/Loading";
import SelectField from "../../../../components/core/basic/SelectField";
import { Accordion } from "../../../../components/core/basic/Accordion";
import { Button } from "../../../../components/core/basic/Button";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { commaSearchFilterFn, customFilterFn } from "../../../../components/table/filterFns";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { Table } from "../../../../components/table/Table";
import { useClientTable } from "../../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState, resetTable } from "../../../../components/table/utils";
import { DatesProvider } from "../../../../dates/DatesContext";
import useQueryParams from "../../../../hooks/useQueryParams";
import { RT8FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useSQPReportRows, useSQPReports } from "../../../../modules/sqpr/api/useSQPReports";
import { SearchQueryRowExpander } from "../../../../modules/sqpr/SearchQueryRowExpander";
import { compareSQPRows, type SQPRRow } from "../../../../modules/sqpr/sqprMetrics";
import { type SQPReport } from "../../../../zodios/apiDefinitions/api/sqpReports";
import { Box, HStack, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";

export const Route = createFileRoute("/_authenticated/user/searchqueryperformance/$id")({
  component: SearchQueryPerformanceDashboard,
});

const columnHelper = createColumnBuilder<SQPRRow>();

const searchQuerySearchFilterFn = commaSearchFilterFn<SQPRRow>((row) => row.search_query);

// Ads stats joined in from the search term report; the rest come from the "searchQuery" entity list.
const AD_METRIC_IDS = ["ad_spend", "ad_sales", "acos", "roas"];

const DEFAULT_VISIBLE_METRIC_IDS = [
  "search_query_score",
  "search_query_volume",
  "impressions_total",
  "impressions_brand_or_asin",
  "impressions_brand_or_asin_share",
  "clicks_total",
];

const reportLabel = (report: SQPReport) =>
  `${report.brand_or_asin} - ${report.reporting_range} (${report.start_date} - ${report.end_date})`;

interface CompareOption {
  value: number;
  label: string;
  details: SQPReport;
}

const CompareReportOption = ({ children, ...props }: OptionProps<CompareOption>) => (
  <components.Option {...props}>
    {children}
    <br />
    {props.data.details.upload && <small className="text-muted">{props.data.details.upload.name}</small>}
  </components.Option>
);

interface ReportProps {
  selectedReport: SQPReport;
  selectedCompareReport: SQPReport | undefined;
}

const SearchQueryRowsTable = ({
  data,
  isLoading,
  selectedReport,
  selectedCompareReport,
}: ReportProps & { data: SQPRRow[] | undefined; isLoading: boolean }) => {
  const { t } = useTranslation(["sqpr", "common"]);
  const searchQueryMetricIds = useStandardEntityMetrics("searchQuery");
  const metricIds = useMemo(() => [...searchQueryMetricIds, ...AD_METRIC_IDS], [searchQueryMetricIds]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("search_query", {
        header: t(($) => $.sqpr["Search Query"]),
        size: 300,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      ...metricIds.map((id) => columnHelper.metric(id)),
    ],
    [t, metricIds],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "sqprRowsTable",
    initialState: {
      sorting: [{ id: "search_query_volume", desc: true }],
      columnVisibility: idsToVisibilityState(DEFAULT_VISIBLE_METRIC_IDS, metricIds),
    },
  });

  const table = useClientTable({
    data,
    columns,
    defaultColumn: { filterFn: customFilterFn },
    getRowId: (row) => row.search_query,
    getRowCanExpand: () => true,
    globalFilterFn: searchQuerySearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: { showSkeleton: isLoading },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        renderExpander={(row) => (
          <SearchQueryRowExpander
            row={row.original}
            selectedReport={selectedReport}
            selectedCompareReport={selectedCompareReport}
          />
        )}
      />
    </>
  );
};

const Dashboard = ({ reports, selectedReport }: { reports: SQPReport[]; selectedReport: SQPReport }) => {
  const { t } = useTranslation(["sqpr"]);
  const [compareReportId, setCompareReportId] = useQueryParams("compare", null);
  const selectedCompareReport = useMemo(
    () => reports.find((r) => String(r.id) === compareReportId),
    [reports, compareReportId],
  );

  const { data: rows, isLoading: isRowsLoading } = useSQPReportRows(selectedReport);
  const { data: compareRows, isLoading: isCompareRowsLoading } = useSQPReportRows(selectedCompareReport);
  const isLoading = isRowsLoading || isCompareRowsLoading;

  const compared = useMemo(() => {
    // Hold the table on its skeleton until a selected compare report has loaded, so it only gets data once.
    if (!rows || (selectedCompareReport && !compareRows)) return undefined;
    return compareSQPRows(rows, compareRows);
  }, [rows, compareRows, selectedCompareReport]);

  const searchQueryMetricIds = useStandardEntityMetrics("searchQuery");
  const metrics = useMemo(
    () => [...searchQueryMetricIds, ...AD_METRIC_IDS].map((id) => getMetricSpec(id)),
    [searchQueryMetricIds],
  );

  const compareOptions = useMemo(
    (): CompareOption[] => reports.map((r) => ({ value: r.id, label: reportLabel(r), details: r })),
    [reports],
  );

  return (
    <MetricsCard
      id="sqpr"
      initialSelectedMetric={DEFAULT_VISIBLE_METRIC_IDS.slice(0, 5)}
      metrics={metrics}
      title={t(($) => $.sqpr["Search Query Performance Reports"])}
      totals={{ data: compared?.total, loading: isLoading }}
      context="search_query_performance"
      showMetricsSelector={true}
      byline={reportLabel(selectedReport)}
    >
      <HStack justifyContent="end">
        <Box width={{ base: "100%", md: "50%", lg: "25%" }}>
          <SelectField
            components={{ Option: CompareReportOption }}
            label={t(($) => $.sqpr["Compare"])}
            options={compareOptions}
            value={compareReportId ? parseInt(compareReportId) : null}
            onChange={(value: number | null) => setCompareReportId(value === null ? null : String(value))}
            isClearable
          />
        </Box>
      </HStack>
      {compared && compared.missingQueries.length > 0 && (
        <Accordion title={t(($) => $.sqpr["Warnings"])}>
          <div className="alert alert-warning">
            <strong>{t(($) => $.sqpr["Warning:"])}</strong>{" "}
            {t(($) => $.sqpr["The following search queries were not found in the report:"])}{" "}
          </div>
          <div>{compared.missingQueries.join(", ")}</div>
        </Accordion>
      )}
      <SearchQueryRowsTable
        data={compared?.rows}
        isLoading={isLoading}
        selectedReport={selectedReport}
        selectedCompareReport={selectedCompareReport}
      />
    </MetricsCard>
  );
};

function SearchQueryPerformanceDashboard() {
  const { t } = useTranslation(["sqpr"]);
  const { id } = Route.useParams();
  const { data: reports, isLoading } = useSQPReports();
  const selectedReport = useMemo(() => reports?.find((r) => String(r.id) === id), [reports, id]);

  if (!isLoading && !selectedReport) {
    return <Navigate to="/user/searchqueryperformance" replace />;
  }

  const breadcrumbs = [
    { name: t(($) => $.sqpr["Search Query Performance"]), href: "/user/searchqueryperformance" },
    ...(selectedReport ? [{ name: reportLabel(selectedReport), href: `/user/searchqueryperformance/${id}` }] : []),
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadcrumbs} hideDateSelector />
      <PageContainer withBreadcrumb>
        <div className="fs-standard">
          {reports && selectedReport ?
            <Dashboard reports={reports} selectedReport={selectedReport} />
          : <Loading />}
        </div>
      </PageContainer>
    </DatesProvider>
  );
}
